<template>
    <div class="package-diff-onboarding">
        <div class="flex justify-between items-center bg-blue-75 rounded-t-3xl p-4 md:p-10 md:pb-6 relative md:h-[156px]">
            <div class="flex flex-col gap-1 relative z-10">
                <h2 class="text-sm text-grey-90">{{ t('editionInfo.onboarding.slogan') }}</h2>
                <p class="text-2xl font-semibold text-black md:text-4xl">{{ t('editionInfo.onboarding.title') }}</p>
                <p class="text-sm text-black">{{ t('editionInfo.onboarding.description') }}</p>
            </div>
            <div class="hidden md:block absolute top-0 right-0">
                <img class="shrink-0 !h-[156px]" :src="Congratulation" alt="congratulation">
            </div>
        </div>
        <div class="bg-white flex flex-col gap-6 rounded-b-3xl p-4 md:px-10 md:pt-6 md:pb-10">
            <div class="text-center">
                <span class="try-experience px-5 text-center text-xl font-semibold mb-4">{{ tryExperienceText }}</span>
            </div>
            <div class="flex gap-4 md:gap-8 flex-col md:flex-row">
                <PackageCard
                    class="order-2 md:order-1"
                    :type="EditionType.FREE"
                    :features="tm('packageDiff.free.features')"
                    :highlight="editionControl.freemium"
                    :showButton="editionControl.freemium"
                    @choose="handleChoose"
                >
                    <template #current>
                        <span v-if="editionControl.freemium" class="absolute top-[2px] right-0 text-sm text-[#8993a4]">{{ t('packageDiff.current') }}</span>
                    </template>
                </PackageCard>

                <PackageCard
                    class="order-1 md:order-2"
                    :type="EditionType.PREMIUM"
                    :features="tm('packageDiff.premium.features')"
                    :highlight="!editionControl.freemium"
                    :showButton="true"
                    @choose="handleChoose"
                >
                    <template #current>
                        <span v-if="isAirPremium" class="absolute top-[2px] right-0 text-sm text-[#8993a4]">{{ t('packageDiff.current') }}</span>
                    </template>
                    <template #button>
                        <span v-if="editionControl.trial" class="absolute bottom-[40px] right-0 text-sm text-[#020a1a] bg-[#ffd02f] rounded-[16px_16px_16px_0] px-3 py-[6px] font-[600]" v-html="t('editionInfo.onboarding.trial', { days: format(editionControl.try_package_left_days) })"></span>
                    </template>
                </PackageCard>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import PackageCard from './OnboardingPackageCard.vue';
import { EditionType, BiTrackPoint, AIR_UPGRADE_EVENT_TYPE } from '../../constants';
import { openClueReport } from '@eureka/clue-report';
import { format } from '@eureka/utils/number-format';
import Congratulation from '../../assets/icon/congratulation.png';
import { useEditionControl } from '../../composables/use-edition-control';
import type { EditionUpgradeAction } from '../../types';
import { cn, en, jp, tw } from '../../infrastructure/locales';
import { useBiTrack } from '../../../../composables/use-bi-track';
import { EurekaPackageType } from '@eureka/services/src/apis/common.api.types';

const { t, tm } = useI18n({
    messages: {
        cn, en, jp, tw,
    },
});
const { editionControl, isAirPremium } = useEditionControl();
const { biTrack } = useBiTrack(AIR_UPGRADE_EVENT_TYPE);
const emits = defineEmits<{
    (event: 'close', action: EditionUpgradeAction): void
}>();

const tryExperienceText = computed(() => {
    if (editionControl.value.trial) return t('editionInfo.onboarding.tryExperienceTrial');
    if (isAirPremium.value) return t('editionInfo.onboarding.tryExperiencePremium');
    return t('editionInfo.onboarding.tryExperience');
});

function handleChoose(isRecommend: boolean) {
    if (isRecommend) {
        biTrack({
            sourceType: BiTrackPoint.AIR_ONBOARDING_UPGRADE_CLICK,
        });
        openClueReport(EurekaPackageType.AIR_COMMERCIAL);
    }
    emits('close', isRecommend ? 'next' : 'close');
}
</script>
